//import {loadUser} from '../../../entities/user';
import history from "../../../constants/global-history";
import {
  SET_AUTH,
  SET_ERROR,
  SET_LOADING,
  SET_SUPPLIER,
  SET_TOKEN,
  SET_USERNAME,
  SET_PROFILE,
  SET_ROLE,
} from "./constants";
import { rememberPrescriberAccessDenied } from "../../../utils/prescriber";
import { clearSessionExpired, markSessionExpired } from "../../../utils/sessionExpiry";

const setLoading = (loading) => {
  return {
    type: SET_LOADING,
    payload: { loading },
  };
};

/**
 * Clears a stuck login spinner after persist/rehydrate or a failed session.
 * @returns {{type: string, payload: {loading: boolean}}}
 */
export const resetAuthLoading = () => setLoading(false);

export const setProfile = (profile) => {
  return {
    type: SET_PROFILE,
    payload: { profile },
  };
};

const setToken = (token) => {
  return async (dispatch, getState, server) => {
    if (token !== null) {
      await server.networkTransport.authenticate(token);
    } else {
      await server.networkTransport.deauthenticate();
    }

    dispatch({
      type: SET_TOKEN,
      payload: { token },
    });
  };
};

const setAuth = (auth) => {
  return {
    type: SET_AUTH,
    payload: { auth },
  };
};

const setRole = (role) => {
  return {
    type: SET_ROLE,
    payload: { role },
  };
};

const setUsername = (username) => {
  return {
    type: SET_USERNAME,
    payload: { username },
  };
};

const setError = (error) => {
  return {
    type: SET_ERROR,
    payload: { error },
  };
};

export const changeSupplier = (supplierId) => {
  return {
    type: SET_SUPPLIER,
    payload: {
      supplierId,
    },
  };
};

/**
 * Signs in with a Firebase ID token and stores the SAYN session JWT.
 * @param {string} email Account email
 * @param {string} firebaseToken Firebase ID token after MFA
 * @param {string} [redirect] Optional path after login
 * @param {boolean} [rememberMe] When true, the session lasts 90 days
 * @returns {Function} Redux thunk
 */
export const login = (email, firebaseToken, redirect, rememberMe) => {
  return async (dispatch, getState, server) => {
    await dispatch(setError(null));
    await dispatch(setLoading(true));

    const settings = {
      method: "POST",
      body: {
        firebaseToken,
        rememberMe: Boolean(rememberMe),
      },
    };

    let response;
    try {
      response = await server.fetch("/user/login", settings);
    } catch (e) {
      await dispatch(setLoading(false));
      throw Error(e);
    }

    const responseJson = await response.json(false);

    let role = null;

    if (response.ok) {
      try {
        await dispatch(setUsername(email));
        await dispatch(setToken(responseJson.token));
        await dispatch(setAuth(true));

        const settingsProfile = {
          method: "GET",
          headers: { Authorization: `Bearer ${responseJson.token}` },
        };
        response = await server.fetch("/user/profile", settingsProfile);
        const profile = await response.json();
        if (!response.ok) {
          throw Error((profile && profile.message) || "PROFILE_ERROR");
        }

        role = profile.role && profile.role.role.toLowerCase();
        if (role) {
          await dispatch(setRole(role));
        } else if (profile.is_admin) {
          role = "admin";
          await dispatch(setRole("admin"));
        } else {
          await dispatch(setRole("client"));
        }

        clearSessionExpired();
        // Reset the login form
      } catch (e) {
        throw Error(e);
      } finally {
        if (role === "prescriber" || role === "owner" || role === "manager") {
          forwardTo("/prescriber/dashboard");
        }
        if (role === "admin" || role === "assistant") {
          forwardTo("/admin/welcome");
        }
        if (redirect) {
          forwardTo(redirect);
        }
        await dispatch(setLoading(false));
      }
    } else {
      await dispatch(setLoading(false));
      throw Error(responseJson.message.toLowerCase());
    }
  };
};

export const logout = (remote) => {
  return async (dispatch, getState, server) => {
    await dispatch(setLoading(true));

    try {
      const token = getState().global && getState().global.auth
        ? getState().global.auth.token
        : null;
      if (token) {
        const settings = {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        };
        await server.fetch("/user/logout", settings);
      }
    } catch (e) {
      // still clear local session if the token is already invalid
    } finally {
      await dispatch(setAuth(false));
      await dispatch(setToken(null));
      await dispatch(setRole("none"));
      if (!remote) {
        forwardTo("/login");
      }
      await dispatch(setLoading(false));
    }
  };
};

export const updatePassword = (password, newPassword) => {
  return async (dispatch, getState, server) => {
    try {
      const settings = {
        method: "POST",
        body: {
          oldPassword: password,
          newPassword: newPassword,
        },
      };

      const response = await server.fetch("/user/password", settings);
      const responseJson = await response.json(false);

      if (response.ok) {
        // Store the token
        await dispatch(setToken(responseJson.token));
        await dispatch(setError(null));
        //await dispatch(loadUser(server));
        return responseJson;
      } else {
        await dispatch(
          setError({
            oldPassword: {
              id: responseJson.message.toLowerCase(),
            },
          }),
        );
      }
    } catch (err) {
      alert(err);
    }
  };
};

export const handleServerError = (server, error) => {
  return async (dispatch, getState, server) => {
    // Logout the user in case the token is invalid or the prescriber was blocked
    if (
      (error.statusCode === 401 &&
        (error.message === "invalid signature" ||
          error.message === "jwt expired")) ||
      (error.statusCode === 403 &&
        (error.message === "USER_DISABLED" ||
          error.message === "OWNER_DISABLED" ||
          error.message === "NO_COMPANY"))
    ) {
      if (
        error.message === "USER_DISABLED" ||
        error.message === "OWNER_DISABLED" ||
        error.message === "NO_COMPANY"
      ) {
        rememberPrescriberAccessDenied(error.message);
      }
      if (
        error.message === "jwt expired" ||
        error.message === "invalid signature"
      ) {
        markSessionExpired();
      }
      await dispatch(logout());
    } else {
      return error.message
    }
  };
};

// Little helper function to abstract going to different pages
const forwardTo = (location) => {
  history.push(location);
};

//Starts the reset password process: resolves with exception on error
export const initResetPassword = (email) => async (dispatch, getState, server) => {
  const settings = {
    method: "POST",
    body: JSON.stringify({
      email,
    }),
  };
  const response = await server.fetch("/user/password/reset", settings);
  const responseJson = await response.json(false);
  if (!response.ok) {
    // await dispatch(setError(responseJson.message));
    throw new Error(responseJson.message);
  }
};

export const resetPasswordConfirm =
  (token, newPassword) => async (dispatch, getState, server) => {
    const settings = {
      method: "POST",
      body: JSON.stringify({
        token,
        newPassword,
      }),
    };
    const response = await server.fetch("/user/password/reset/confirm", settings);
    const responseJson = await response.json(false);
    if (!response.ok) {
      throw new Error(responseJson.message);
      // await dispatch(setError(responseJson.message));
    }
  };
